import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { useParams } from "react-router";
import moment from "moment";
import { ArrowUp, Square, Mic, LayoutGrid } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeHighlight from "rehype-highlight";
import { Element, scroller } from "react-scroll";
import { ComponentType } from "react";
import { motion, AnimatePresence } from "framer-motion";

import Logo from "../assets/images/dealer-logo-dark.png";
import LogoDark from "../assets/images/dealer-logo.png";

import withLoader from "../redux/Hoc/withLoader";
import Api, { getBotBaseURL, getToken } from "../redux/api";
import withSession from "../redux/Hoc/withSession";
import { sortMessagesAsc } from "../helpers";
import { StreamApi } from "../helpers/Streaming";
import ThinkingAnimation from "../elements/ThinkingAnimation";
import VoiceModeScreen from "./VoiceModeScreen";
import PromptsModal from "../elements/PromptsModal";
import ChatOptionsBar from "../elements/ChatOptionsBar";
import {
  normalizeStreamOptions,
  NormalizedOptions,
} from "../helpers/chatOptions";

// Long press hook
function useLongPress(callback = () => {}, ms = 500) {
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const start = () => {
    timerRef.current = setTimeout(() => {
      callback();
      setLongPressTriggered(true);
    }, ms);
  };

  const clear = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setLongPressTriggered(false);
  };

  return {
    onMouseDown: start,
    onMouseUp: clear,
    onMouseLeave: clear,
    onTouchStart: start,
    onTouchEnd: clear,
  };
}

// Export chatRef for sidebar integration
export const chatRef = React.createRef<{
  handleSendQuestion: (question: string) => void;
}>();

interface Message {
  id: string;
  message: string;
  type: "ai" | "human";
  timestamp: {
    _seconds: number;
    _nanoseconds: number;
  };
}

interface ChatPromptScreenProps {
  session: string;
  loader: boolean;
  setSession: (session: string) => void;
  setLoader: (value: boolean) => void;
}

// Memoized animation variants for better performance
const shimmerVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const thinkingAnimationVariants = {
  initial: { opacity: 0, y: 20, scale: 0.95 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
  exit: { opacity: 0, y: -20, scale: 0.95 },
};

const thinkingAnimationTransition = {
  duration: 0.4,
  ease: "easeInOut" as const,
  opacity: { duration: 0.3 },
  y: { duration: 0.4 },
  scale: { duration: 0.3 },
};

const faqItemVariants = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
};

// Common configurations
const markdownPlugins = {
  remarkPlugins: [remarkGfm],
  rehypePlugins: [rehypeRaw, rehypeHighlight],
};

const scrollConfig = {
  smooth: true,
  containerId: "ContainerElementID",
  offset: 0,
};

// Logo component to avoid duplication
const LogoComponent = ({
  className = "h-10 md:h-20",
}: {
  className?: string;
}) => {
  const longPressEvents = useLongPress(() => {
    window.location.href = "/";
  }, 1000);

  return (
    <button {...longPressEvents} className="relative">
      <motion.img
        className={`${className} hidden dark:block`}
        src={Logo}
        alt="ivin logo"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      />
      <motion.img
        className={`${className} dark:hidden`}
        src={LogoDark}
        alt="ivin logo"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      />
    </button>
  );
};

const FAQItem = React.memo(
  ({
    item,
    index,
    onSelect,
  }: {
    item: { prompt: string; type: string; order: number };
    index: number;
    onSelect: (prompt: string) => void;
  }) => (
    <motion.div
      variants={faqItemVariants}
      initial="initial"
      animate="animate"
      transition={{ duration: 0.3, delay: index * 0.1 }}
      className="w-full"
    >
      <button
        type="button"
        className="w-full h-24 flex flex-col justify-center border border-gray-200 dark:border-secondary rounded-2xl p-4 hover:bg-gray-50 dark:hover:bg-secondary hover:border-gray-300 dark:hover:border-tertiary transition-all group bg-white dark:bg-primary shadow-sm hover:shadow-lg hover:scale-105"
        onClick={() => onSelect(item.prompt)}
      >
        <div className="flex items-center justify-between h-full">
          <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 text-left flex-1 leading-relaxed">
            {item.prompt}
          </p>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity ml-3 flex-shrink-0">
            <ArrowUp
              className="rotate-90 text-gray-400 dark:text-gray-500"
              size={20}
            />
          </div>
        </div>
      </button>
    </motion.div>
  ),
);

const FAQShimmer = React.memo(({ index }: { index: number }) => (
  <motion.div
    key={index}
    variants={shimmerVariants}
    initial="initial"
    animate="animate"
    transition={{ duration: 0.3, delay: index * 0.1 }}
    className="w-full h-24 md:h-28 flex flex-col justify-center border border-gray-200 dark:border-secondary rounded-2xl p-6 bg-white dark:bg-primary shadow-sm animate-pulse"
  >
    <div className="h-4 bg-gray-200 dark:bg-secondary rounded w-3/4 mb-2"></div>
    <div className="h-3 bg-gray-100 dark:bg-secondary rounded w-1/2"></div>
  </motion.div>
));

const ChatPromptScreen: React.FC<ChatPromptScreenProps> = ({
  session,
  loader,
  setLoader,
  setSession,
}) => {
  const { id } = useParams();

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastItemRef = useRef<HTMLDivElement | null>(null);
  const secondLastItemRef = useRef<HTMLDivElement | null>(null);

  const [heights, setHeights] = useState({
    containerHeight: 0,
    lastHeight: 0,
    secondLastHeight: 0,
  });

  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [showVoiceMode, setShowVoiceMode] = useState(false);
  const [showPromptsModal, setShowPromptsModal] = useState(false);

  const handleOpenVoiceMode = useCallback(async () => {
    if (!session || !session.includes("-#$#-")) {
      try {
        const response = await Api.post(`/ai/chat-history`);
        setSession(`${id}-#$#-${response.data.data.sessionId}`);
      } catch (err) {
        console.error("[VoiceMode] Failed to create session:", err);
      }
    }
    setShowVoiceMode(true);
  }, [session, id, setSession]);

  const [faqItems, setFAQItems] = useState<
    { prompt: string; type: string; order: number }[]
  >([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState<string>("");

  const [aiTypingMessage, setAiTypingMessage] = useState("");
  const [activeOptions, setActiveOptions] = useState<NormalizedOptions>(null);
  const typingQueue = useRef<string[]>([]);
  const typingInterval = useRef<NodeJS.Timeout | null>(null);
  const messageCountRef = useRef<number>(0);

  const handleSend = useCallback(async () => {
    if (!input.trim()) return;
    setLoader(true);
    setActiveOptions(null);

    // Convert any existing aiTypingMessage to a permanent message before starting new conversation
    if (aiTypingMessage.trim()) {
      setMessages((prev) => {
        const newMessages = [
          ...prev,
          {
            id: `${Date.now()}_ai_typing`,
            message: aiTypingMessage,
            type: "ai" as const,
            timestamp: {
              _seconds: moment().unix(),
              _nanoseconds: moment().milliseconds() * 1e6,
            },
          },
        ];
        messageCountRef.current = newMessages.length;
        return newMessages;
      });
    }

    // Clear any existing typing message and intervals
    setAiTypingMessage("");
    if (typingInterval.current) {
      clearInterval(typingInterval.current);
      typingInterval.current = null;
    }
    typingQueue.current = [];

    let sessionId = "";
    let streaming = "";

    // Add the human message first
    setMessages((prev) => {
      const newMessages = [
        ...prev,
        {
          id: `${Date.now()}_human`,
          message: input,
          type: "human" as const,
          timestamp: {
            _seconds: moment().unix(),
            _nanoseconds: moment().milliseconds() * 1e6,
          },
        },
      ];
      messageCountRef.current = newMessages.length;

      // Scroll to bottom when human sends a new message (like when messages are first loaded)
      setTimeout(() => {
        scroller.scrollTo((messageCountRef.current - 1).toString(), {
          ...scrollConfig,
          offset: -80,
        });
      }, 100);

      return newMessages;
    });

    setInput("");

    // Handle session creation or extraction
    if (!session || !session.includes("-#$#-")) {
      try {
        const response = await Api.post(`/ai/chat-history`);
        const { data } = response;
        sessionId = data.data.sessionId;
        setSession(`${id}-#$#-${data.data.sessionId}`);
      } catch (err) {
        console.error("Error creating session:", err);
        setLoader(false);
        return;
      }
    } else {
      sessionId = session.split("-#$#-")[1];
    }

    StreamApi({
      chatId: sessionId,
      msg: input,
      callback: (data) => {
        if (data.text) {
          streaming += data.text;
          typingQueue.current.push(...data.text.split(""));

          if (!typingInterval.current) {
            setLoader(false); // Set loader to false when typing starts
            typingInterval.current = setInterval(() => {
              const nextChar = typingQueue.current.shift();
              if (nextChar !== undefined) {
                setAiTypingMessage((prev) => prev + nextChar);
              }

              if (typingQueue.current.length === 0 && typingInterval.current) {
                clearInterval(typingInterval.current);
                typingInterval.current = null;
              }
            }, 10);
          }
        }

        if (data.end) {
          // Calculate how long the typing animation will take
          const typingDuration = streaming.length * 10; // 10ms per character
          const minWaitTime = Math.max(typingDuration, 1000); // At least 1 second

          setTimeout(() => {
            setMessages((prev) => {
              const newMessages = [
                ...prev,
                {
                  id: `${Date.now()}_ai`,
                  message: streaming,
                  type: "ai" as const,
                  timestamp: {
                    _seconds: moment().unix(),
                    _nanoseconds: moment().milliseconds() * 1e6,
                  },
                },
              ];
              messageCountRef.current = newMessages.length;

              // Scroll to the correct position after AI response
              setTimeout(() => {
                scroller.scrollTo(
                  (messageCountRef.current - 1).toString(),
                  scrollConfig,
                );
              }, 200);

              return newMessages;
            });

            setActiveOptions(normalizeStreamOptions(data.options));
            setAiTypingMessage("");
            textAreaRef.current?.focus();
          }, minWaitTime);
        }
      },
    });
  }, [input, session, id, setSession, setLoader, aiTypingMessage]);

  const handleSendQuestion = useCallback(
    async (question: string) => {
      if (!question.trim() || loader) return;

      setLoader(true);
      setActiveOptions(null);

      // Convert any existing aiTypingMessage to a permanent message before starting new conversation
      if (aiTypingMessage.trim()) {
        setMessages((prev) => {
          const newMessages = [
            ...prev,
            {
              id: `${Date.now()}_ai_typing`,
              message: aiTypingMessage,
              type: "ai" as const,
              timestamp: {
                _seconds: moment().unix(),
                _nanoseconds: moment().milliseconds() * 1e6,
              },
            },
          ];
          messageCountRef.current = newMessages.length;
          return newMessages;
        });
      }

      // Clear any existing typing message and intervals
      setAiTypingMessage("");
      if (typingInterval.current) {
        clearInterval(typingInterval.current);
        typingInterval.current = null;
      }
      typingQueue.current = [];

      let sessionId = "";
      let streaming = "";

      // Add the human message first
      setMessages((prev) => {
        const newMessages = [
          ...prev,
          {
            id: `${Date.now()}_human`,
            message: question,
            type: "human" as const,
            timestamp: {
              _seconds: moment().unix(),
              _nanoseconds: moment().milliseconds() * 1e6,
            },
          },
        ];
        messageCountRef.current = newMessages.length;

        // Scroll to bottom when human sends a new message (like when messages are first loaded)
        setTimeout(() => {
          scroller.scrollTo((messageCountRef.current - 1).toString(), {
            ...scrollConfig,
            offset: -80,
          });
        }, 100);

        return newMessages;
      });

      // Handle session creation or extraction
      if (!session || !session.includes("-#$#-")) {
        try {
          const response = await Api.post(`/ai/chat-history`);
          const { data } = response;
          sessionId = data.data.sessionId;
          setSession(`${id}-#$#-${data.data.sessionId}`);
        } catch (err) {
          console.error("Error creating session:", err);
          setLoader(false);
          return;
        }
      } else {
        sessionId = session.split("-#$#-")[1];
      }

      StreamApi({
        chatId: sessionId,
        msg: question,
        callback: (data) => {
          if (data.text) {
            streaming += data.text;
            typingQueue.current.push(...data.text.split(""));

            if (!typingInterval.current) {
              setLoader(false); // Set loader to false when typing starts
              typingInterval.current = setInterval(() => {
                const nextChar = typingQueue.current.shift();
                if (nextChar !== undefined) {
                  setAiTypingMessage((prev) => prev + nextChar);
                }

                if (
                  typingQueue.current.length === 0 &&
                  typingInterval.current
                ) {
                  clearInterval(typingInterval.current);
                  typingInterval.current = null;
                }
              }, 10);
            }
          }

          if (data.end) {
            // Calculate how long the typing animation will take
            const typingDuration = streaming.length * 10; // 10ms per character
            const minWaitTime = Math.max(typingDuration, 1000); // At least 1 second

            setTimeout(() => {
              setMessages((prev) => {
                const newMessages = [
                  ...prev,
                  {
                    id: `${Date.now()}_ai`,
                    message: streaming,
                    type: "ai" as const,
                    timestamp: {
                      _seconds: moment().unix(),
                      _nanoseconds: moment().milliseconds() * 1e6,
                    },
                  },
                ];
                messageCountRef.current = newMessages.length;

                // Scroll to the correct position after AI response
                setTimeout(() => {
                  scroller.scrollTo(
                    (messageCountRef.current - 1).toString(),
                    scrollConfig,
                  );
                }, 200);

                return newMessages;
              });

              setActiveOptions(normalizeStreamOptions(data.options));
              setAiTypingMessage("");
              textAreaRef.current?.focus();
            }, minWaitTime);
          }
        },
      });
    },
    [session, id, setSession, setLoader, aiTypingMessage, loader],
  );

  // Expose handleSendQuestion to parent components via ref
  React.useImperativeHandle(
    chatRef,
    () => ({
      handleSendQuestion,
    }),
    [handleSendQuestion],
  );

  const fetchMessages = useCallback(async () => {
    if (!id || !session) {
      return;
    }

    setLoader(true);
    try {
      const sessionId = session.split("-#$#-")[1];

      const res = await Api.get(`/ai/chat-history/${sessionId}/messages`);

      const convertedArray = res?.data?.data?.map((item: any) => ({
        id: item.id,
        message: item.message,
        type: item.type,
        timestamp: item.timestamp,
      }));

      const sortedMessages = sortMessagesAsc(convertedArray);
      setMessages(sortedMessages);
      messageCountRef.current = sortedMessages.length;

      setTimeout(() => {
        scroller.scrollTo((sortedMessages.length - 1).toString(), {
          ...scrollConfig,
          offset: -80,
        });
        textAreaRef.current?.focus();
      }, 100);
    } catch (err) {
      console.error("Error fetching messages:", err);
    } finally {
      setLoader(false);
    }
  }, [id, session, setLoader]);

  const fetchFaq = useCallback(async () => {
    try {
      const res = await Api.get(`/ai/app-settings`);
      const { data } = res;

      if (data?.data?.question_list && Array.isArray(data.data.question_list)) {
        const shuffled = data.data.question_list.sort(
          () => 0.5 - Math.random(),
        );
        const selected = shuffled.slice(0, 4);
        setFAQItems(selected);
      } else {
        console.warn("Invalid FAQ data received:", data);
        setFAQItems([]);
      }
    } catch (err) {
      console.error("Error fetching FAQ items:", err);
      setFAQItems([]);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    setGeneratingSummary(true);
    try {
      const botBaseURL = (await getBotBaseURL()) || "";
      const token = (await getToken()) || "";
      const response = await fetch(
        botBaseURL.replace("/chat", "") + `/trigger-llm-summaries`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();
      // console.log("Summary:", data);
    } catch (err) {
      console.error("Error fetching messages:", err);
    } finally {
      setGeneratingSummary(false);
    }
  }, []);

  useEffect(() => {
    if (typingInterval.current) {
      clearInterval(typingInterval.current);
      typingInterval.current = null;
    }
    typingQueue.current = [];

    // Check if we have a valid session for this route
    if (session && session.includes("-#$#-")) {
      const routeID = session.split("-#$#-")[0];
      if (routeID === id) {
        fetchMessages();
        return;
      } else {
        setSession("");
      }
    }

    // If no valid session, clear session and fetch FAQ
    setSession("");
    fetchFaq();
  }, [id]);

  useLayoutEffect(() => {
    const containerHeight = containerRef.current?.offsetHeight ?? 0;
    const lastHeight = lastItemRef.current?.offsetHeight ?? 0;
    const secondLastHeight = secondLastItemRef.current?.offsetHeight ?? 0;

    setHeights({
      containerHeight,
      lastHeight,
      secondLastHeight,
    });
  }, [messages]);

  // Cleanup effect to clear intervals when component unmounts
  useEffect(() => {
    return () => {
      if (typingInterval.current) {
        clearInterval(typingInterval.current);
        typingInterval.current = null;
      }
    };
  }, []);

  useEffect(() => {
    fetchSummary();
  }, []);

  return (
    <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-primary">
      <div
        className={`flex flex-col items-center justify-between w-full h-full max-w-4xl p-4 transition-all ${
          messages?.length > 0 ? "" : ""
        }`}
      >
        {generatingSummary && (
          <div className="flex items-center justify-center rounded-full p-4 border border-gray-200 bg-gray-50 dark:bg-primary shadow-sm">
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Generating summary...
            </p>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden md:flex flex-col gap-8 text-center my-12"
        >
          <div className="flex mx-auto items-center justify-center">
            <LogoComponent
              className={`${
                messages?.length > 0 ? "h-12 md:h-16" : ""
              } transition-all duration-300`}
            />
          </div>
        </motion.div>
        <div
          id="ContainerElementID"
          ref={containerRef}
          className={`w-full h-full flex flex-col overflow-y-auto px-3 transition-all flex-1 ${
            messages?.length > 0 ? "" : "items-center justify-center"
          }`}
          style={{ maxHeight: "calc(100vh - 200px)" }}
        >
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-5 pt-36 md:pt-0">
              <div className="text-center space-y-4">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
                  Ask iVin
                </h2>
                <p className="text-gray-600 dark:text-gray-300 text-base md:text-md max-w-2xl mx-auto leading-relaxed">
                  Your smart vehicle information assistant. Get detailed
                  insights on any car by VIN, check history, specifications, and
                  more — or start with the prompts below.
                </p>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="w-full max-w-7xl"
              >
                {!loader && faqItems?.length > 0 && (
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-8 text-center">
                    Popular Questions
                  </h3>
                )}
                <div className="w-full">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                    {!loader
                      ? faqItems?.map((item, index) => (
                          <FAQItem
                            key={`${item.prompt}-${index}`}
                            item={item}
                            index={index}
                            onSelect={handleSendQuestion}
                          />
                        ))
                      : [...Array(4)].map((_, index) => (
                          <div key={index} className="w-full">
                            <FAQShimmer index={index} />
                          </div>
                        ))}
                  </div>
                </div>
              </motion.div>
            </div>
          ) : (
            <div className="w-full max-w-4xl mx-auto">
              {messages && messages.length > 0 ? (
                messages.map((msg: Message, index: number) => {
                  const isHuman = msg.type === "human";
                  const isAi = msg.type === "ai";
                  const isSecondLast = index === messages.length - 2;
                  const isLast = index === messages.length - 1;

                  const ScrollElement = Element as ComponentType<any>;
                  return (
                    <ScrollElement
                      id={index.toString()}
                      name={index.toString()}
                      key={index.toString()}
                    >
                      <div
                        ref={
                          isLast
                            ? lastItemRef
                            : isSecondLast
                              ? secondLastItemRef
                              : null
                        }
                        className="w-full my-2.5"
                        style={{
                          height:
                            isLast && isHuman
                              ? `${heights.containerHeight}px`
                              : isSecondLast && isHuman
                                ? "auto"
                                : isLast && isAi
                                  ? `${
                                      heights.containerHeight -
                                      heights.secondLastHeight
                                    }px`
                                  : "auto",
                        }}
                      >
                        <div
                          className={`w-full flex ${
                            msg?.type === "human"
                              ? "justify-end"
                              : "justify-start"
                          }`}
                        >
                          <div
                            className={`relative w-full md:max-w-[80%] ${
                              msg?.type === "human"
                                ? "py-4 px-6 bg-secondary text-white text-base rounded-xl"
                                : "markdown dark:text-white text-gray-700 rounded-tl-none text-base"
                            }`}
                          >
                            {msg?.type === "ai" ? (
                              <ReactMarkdown
                                children={msg?.message}
                                {...markdownPlugins}
                              />
                            ) : (
                              msg?.message
                            )}
                          </div>
                        </div>
                        {isLast && msg?.type === "human" && aiTypingMessage && (
                          <div className={"w-full flex justify-start"}>
                            <div
                              className={
                                "relative max-w-[80%] markdown dark:text-white text-gray-700 rounded-tl-none text-base"
                              }
                            >
                              <ReactMarkdown
                                children={aiTypingMessage}
                                {...markdownPlugins}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </ScrollElement>
                  );
                })
              ) : (
                <div className="flex items-center justify-center h-32">
                  <p className="text-gray-500 dark:text-gray-400">
                    No messages yet
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="w-full flex flex-col gap-3 items-center justify-center">
          <motion.div
            variants={thinkingAnimationVariants}
            initial="initial"
            animate={loader && messages.length > 0 ? "animate" : "exit"}
            transition={thinkingAnimationTransition}
            className="w-full flex items-center justify-center"
          >
            {loader && messages.length > 0 && (
              <ThinkingAnimation
                query={input}
                interval={2000}
                maxContexts={2}
              />
            )}
          </motion.div>
          {activeOptions && (
            <ChatOptionsBar
              options={activeOptions}
              onSelect={handleSendQuestion}
              disabled={loader}
            />
          )}
          <div className="w-full min-h-36 max-h-42 flex bg-white dark:bg-primary drop-shadow-lg rounded-3xl border border-gray-200 dark:border-secondary p-4 gap-4">
            <textarea
              ref={textAreaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = "20px";
                e.target.style.height = `${Math.min(
                  e.target.scrollHeight,
                  200,
                )}px`;
              }}
              disabled={loader}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask me anything about your car..."
              className="flex-1 min-h-30 max-h-42 bg-transparent disabled:opacity-50 !outline-none text-gray-700 dark:text-gray-300 resize-none overflow-hidden transition-all placeholder-gray-400 dark:placeholder-gray-500"
            />
            <div className="flex flex-col gap-2">
              {/* Prompts modal trigger */}
              <button
                type="button"
                title="Browse prompts"
                className="w-10 h-10 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-secondary transition-colors"
                onClick={() => setShowPromptsModal(true)}
              >
                <LayoutGrid size={18} />
              </button>
              {/* Voice mode trigger */}
              <button
                type="button"
                title="Voice mode"
                className="w-10 h-10 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-secondary transition-colors"
                onClick={handleOpenVoiceMode}
              >
                <Mic size={18} />
              </button>
              {/* Send */}
              <button
                type="button"
                disabled={loader}
                className="bg-tertiary hover:bg-tertiary/90 w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-50 transition-colors"
                onClick={handleSend}
              >
                {loader ? (
                  <Square
                    color="white"
                    fill="white"
                    strokeWidth={2}
                    size={22}
                  />
                ) : (
                  <ArrowUp color="white" strokeWidth={2} size={22} />
                )}
              </button>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-400/30 px-2 font-inter">
              iVin can make mistakes. Double check important information.
            </p>
          </div>
        </div>
      </div>

      {/* Voice Mode overlay */}
      <AnimatePresence>
        {showVoiceMode && (
          <VoiceModeScreen
            onClose={() => setShowVoiceMode(false)}
            session={session}
          />
        )}
      </AnimatePresence>

      {/* Prompts modal */}
      <PromptsModal
        isOpen={showPromptsModal}
        onClose={() => setShowPromptsModal(false)}
        onSelectQuestion={handleSendQuestion}
        loader={loader}
      />
    </div>
  );
};

export default withLoader(withSession(ChatPromptScreen));
